<template>
  <v-dialog v-model="dialog" persistent :width="withTime ? '600px' : '290px'">
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-bind="attrs"
        v-on="on"
        :append-icon="withTime ? 'mdi-calendar-clock' : 'mdi-calendar'"
        readonly
        :value="dateRangeText"
        :label="label"
        clearable
        @click:clear="handleClear"
        outlined
        class="rounded-pill"
        :error-messages="isEmpty ? errorMessages : ''"
        :disabled="disabled"
        dense
      />
    </template>
    <v-card v-if="withTime">
      <v-card-title class="text-h6">Select Date & Time</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-date-picker
              v-model="selectedDate"
              full-width
              scrollable
              :min="minDateComputed"
              :max="maxDateComputed"
            />
          </v-col>
          <v-col cols="6">
            <v-menu
              ref="startTimeMenu"
              v-model="startTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startTime"
                  label="Start Time"
                  prepend-icon="mdi-clock-time-four-outline"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                />
              </template>
              <v-time-picker
                v-if="startTimeMenu"
                v-model="startTime"
                full-width
                format="24hr"
                @click:minute="$refs.startTimeMenu.save(startTime)"
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              ref="endTimeMenu"
              v-model="endTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endTime"
                  label="End Time"
                  prepend-icon="mdi-clock-time-four-outline"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                />
              </template>
              <v-time-picker
                v-if="endTimeMenu"
                v-model="endTime"
                full-width
                format="24hr"
                @click:minute="$refs.endTimeMenu.save(endTime)"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" @click="handleCancel">Cancel</v-btn>
        <v-btn text color="primary" @click="handleDateTimeConfirm" :disabled="isDateTimeInvalid">OK</v-btn>
      </v-card-actions>
    </v-card>
    <v-date-picker
      v-else
      v-model="localValue"
      range
      scrollable
      :allowed-dates="allowedDates"
      @change="handleDateChange"
      :min="minDateComputed"
      :max="maxDateComputed"
    >
      <v-spacer />
      <v-btn text color="primary" @click="handleCancel">Cancel</v-btn>
      <v-btn text color="primary" @click="handleDateConfirm">OK</v-btn>
    </v-date-picker>
  </v-dialog>
</template>

<script>
import { getTommorowDate } from '../../../../../lib/helpers';

export default {
  name: "DateRangeFilter",
  props: {
    value: {
      type: Array || String,
      default: () => [] || "",
    },
    label: {
      type: String,
      default: "Select Date Range",
    },
    includeToday: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
      default: "",
    },
    minDateOffset: {
      type: Number,
      default: null,
    },
    errorMessages: {
      type: String,
      default: "",
    },
    limitRangeTo30Days: {
      type: Boolean,
      default: false,
    },
    maxRangeDays: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    defaultToday: {
      type: Boolean,
      default: false,
    },
    withTime: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    let initialValue = [];
    let selectedDate = '';
    let startTime = '00:00';
    let endTime = '23:59';
    
    if (this.withTime) {
      if (this.defaultToday && (!this.value || this.value.length === 0)) {
        selectedDate = today;
        startTime = '00:00';
        endTime = '23:59';
        initialValue = [`${today} 00:00:00`, `${today} 23:59:59`];
      } else if (this.value && this.value.length > 0) {
        // Parse existing datetime value
        const startDateTime = this.value[0] || '';
        const endDateTime = this.value[1] || '';
        if (startDateTime) {
          const parts = startDateTime.split(' ');
          selectedDate = parts[0] || '';
          const time = parts[1] || '';
          startTime = time ? time.substring(0, 5) : '00:00';
        }
        if (endDateTime) {
          const parts = endDateTime.split(' ');
          const time = parts[1] || '';
          endTime = time ? time.substring(0, 5) : '23:59';
        }
        initialValue = [...this.value];
      }
    } else if (this.defaultToday && (!this.value || this.value.length === 0)) {
      initialValue = [today, today];
    } else if (this.value) {
      initialValue = [...this.value];
    } else {
      initialValue = [];
    }
    
    return {
      dialog: false,
      localValue: initialValue,
      selectedDate,
      startTime,
      endTime,
      startTimeMenu: false,
      endTimeMenu: false,
      getTommorowDate,
      originalValue: [],
      originalSelectedDate: '',
      originalStartTime: '00:00',
      originalEndTime: '23:59',
    };
  },
  created() {
    if (this.defaultToday && (!this.value || this.value.length === 0)) {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      if (this.withTime) {
        const defaultValue = [`${today} 00:00:00`, `${today} 23:59:59`];
        this.localValue = defaultValue;
        this.selectedDate = today;
        this.startTime = '00:00';
        this.endTime = '23:59';
        this.$emit('input', defaultValue);
      } else {
        this.$emit('input', [today, today]);
      }
    }
  },
  watch: {
    value(newVal) {
      if (!newVal || newVal.length === 0) {
        this.localValue = [];
        this.selectedDate = '';
        this.startTime = '00:00';
        this.endTime = '23:59';
        return;
      }
      
      if (this.withTime && newVal.length > 0) {
        const startDateTime = newVal[0] || '';
        const endDateTime = newVal[1] || '';
        if (startDateTime) {
          const parts = startDateTime.split(' ');
          this.selectedDate = parts[0] || '';
          const time = parts[1] || '';
          this.startTime = time ? time.substring(0, 5) : '00:00';
        }
        if (endDateTime) {
          const parts = endDateTime.split(' ');
          const time = parts[1] || '';
          this.endTime = time ? time.substring(0, 5) : '23:59';
        }
      }
      this.localValue = [...newVal];
    },
    dialog(isOpen) {
      if (isOpen) {
        this.originalValue = this.localValue ? [...this.localValue] : [];
        this.originalSelectedDate = this.selectedDate;
        this.originalStartTime = this.startTime;
        this.originalEndTime = this.endTime;
      }
    },
  },
  computed: {
    allowedDates() {
      const rangeDays = this.maxRangeDays ?? (this.limitRangeTo30Days ? 30 : null);
      if (rangeDays === null) return () => true;

      if (!this.localValue || this.localValue.length === 0) return () => true;

      const baseDate = new Date(this.localValue[0]);
      const min = new Date(baseDate);
      const max = new Date(baseDate);
      min.setDate(min.getDate() - rangeDays);
      max.setDate(max.getDate() + rangeDays);

      return (date) => {
        const current = new Date(date);
        return current >= min && current <= max;
      };
    },

    isEmpty() {
      return !this.localValue || this.localValue.length === 0;
    },
    dateRangeText() {
      if (this.localValue.length > 0) {
        if (this.withTime) {
          // Format: "2026-03-16 00:00 - 23:59"
          const start = this.localValue[0] || '';
          const end = this.localValue[1] || '';
          if (start && end) {
            const [startDate, startTime] = start.split(' ');
            const [, endTime] = end.split(' ');
            const startTimeShort = startTime ? startTime.substring(0, 5) : '';
            const endTimeShort = endTime ? endTime.substring(0, 5) : '';
            return `${startDate} ${startTimeShort} - ${endTimeShort}`;
          }
        } else {
          const [start, end] = this.localValue;
          return `${start} - ${end || start}`;
        }
      }

      return "";
    },
    maxDateComputed() {
      if (this.maxDate) return this.maxDate;

      const today = new Date();
      const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      return this.includeToday
        ? localDate
        : getTommorowDate(-1); 
    }
    ,

    minDateComputed() {
      if (this.minDate) return this.minDate;
      if (this.minDateOffset !== null) {
        const date = new Date();
        date.setDate(date.getDate() - this.minDateOffset);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
      return null;
    },
    
    isDateTimeInvalid() {
      if (!this.withTime) return false;
      return !this.selectedDate || !this.startTime || !this.endTime;
    },
  },
  methods: {
    clearError() {
      if (this.errorMessages) {
        this.$emit("clear-error");
      }
    },
    handleClear() {
      this.localValue = [];
      this.selectedDate = '';
      this.startTime = '00:00';
      this.endTime = '23:59';
      this.$emit("input", []);
    },

    handleDateChange(date) {
      if (date.length === 1) {
        this.localValue = [date[0]];
      } else if (date.length === 2) {
        const [start, end] = date[0] <= date[1] ? date : [date[1], date[0]];
        this.localValue = [start, end];
      }
    },
    handleDateTimeConfirm() {
      if (!this.selectedDate) {
        this.dialog = false;
        return;
      }
      
      // Format: ["2026-03-16 00:00:00", "2026-03-16 23:59:59"]
      const startDateTime = `${this.selectedDate} ${this.startTime}:00`;
      const endDateTime = `${this.selectedDate} ${this.endTime}:59`;
      
      this.localValue = [startDateTime, endDateTime];
      this.clearError();
      this.$emit("input", [startDateTime, endDateTime]);
      this.dialog = false;
    },
    handleDateConfirm() {
      if (this.localValue.length === 2) {
        const [start, end] = this.localValue;

        this.localValue = start <= end ? [start, end] : [end, start];
      }
      if (this.localValue.length === 1) {
        this.localValue = [this.localValue[0], this.localValue[0]];
      }
      this.clearError();
      this.$emit("input", [...this.localValue]);
      this.dialog = false;
    },
    handleCancel() {
      this.localValue = this.originalValue ? [...this.originalValue] : [];
      this.selectedDate = this.originalSelectedDate;
      this.startTime = this.originalStartTime;
      this.endTime = this.originalEndTime;
      this.dialog = false;
    },
  },
};
</script>
